import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import Context from 'managers/Context';
import { getActiveCrud } from 'utils/crud';
import {
    filterDetailExtraFieldsByDynamism,
    getDetailFieldPropsById,
    isDetailFieldReadOnly,
    isDetailFieldVisible,
    isDetailStandardFieldVisible,
    isShadowCrudReadyState,
} from 'utils/fm/detailFieldDynamism';

export const getDetailFieldPropsFromStore = (fieldId, isNewEntity = false) => {
    const entityCrud = getActiveCrud(Context.store.getState()) || {};

    return getDetailFieldPropsById(fieldId, {
        data: entityCrud.data,
        configMap: entityCrud.configMap,
        mappedSchema: entityCrud.mappedSchema,
        dynamicHiddenMap: entityCrud.dynamicHiddenMap,
        isNewEntity,
    });
};

const getCrudKey = (entityCrud) => {
    if (!entityCrud?.entity) return null;
    if (entityCrud.id != null && entityCrud.id !== '') {
        return `${entityCrud.entity}-${entityCrud.id}`;
    }
    return entityCrud.entity;
};

/**
 * Detail widgets subscribe to shadow CRUD for SmartField/dynamism parity.
 * After save, EntityCrud re-inits (loading + cleared data) which would briefly
 * drop readiness and fall back to backend hiddenByDynamism — a visible redraw.
 * Keep the last ready context for the same entity until the new init succeeds.
 */
export const useDetailFieldDynamism = ({ isNewEntity = false } = {}) => {
    const entityCrud = useSelector((state) => getActiveCrud(state)) || {};
    const extraFieldsSchemaMap = useSelector((state) => state.config?.extraFieldsSchemaMap || {});
    const { data, configMap, mappedSchema, dynamicHiddenMap, loading } = entityCrud;
    const crudKey = getCrudKey(entityCrud);

    const lastReadyRef = useRef({ crudKey: null, context: null });

    const isLiveReady = isShadowCrudReadyState({
        mappedSchema,
        configMap,
        data,
        loading,
    });

    const liveContext = useMemo(
        () => ({
            data,
            configMap,
            mappedSchema,
            dynamicHiddenMap,
            isNewEntity,
            extraFieldsSchemaMap,
            loading,
        }),
        [
            data,
            configMap,
            mappedSchema,
            dynamicHiddenMap,
            isNewEntity,
            extraFieldsSchemaMap,
            loading,
        ],
    );

    if (lastReadyRef.current.crudKey !== crudKey) {
        lastReadyRef.current = { crudKey, context: null };
    }

    if (isLiveReady) {
        lastReadyRef.current = { crudKey, context: liveContext };
    }

    const staleContext = lastReadyRef.current.context;
    const dynamismContext = isLiveReady ? liveContext : staleContext || liveContext;
    const hasEffectiveShadowCrud = Boolean(isLiveReady || staleContext);

    const getFieldProps = useCallback(
        (fieldId) => getDetailFieldPropsById(fieldId, dynamismContext),
        [dynamismContext],
    );

    const isFieldVisible = useCallback(
        (fieldId) => isDetailFieldVisible(getFieldProps(fieldId)),
        [getFieldProps],
    );

    const isFieldReadOnly = useCallback(
        (fieldId) => isDetailFieldReadOnly(getFieldProps(fieldId)),
        [getFieldProps],
    );

    const isStandardFieldVisible = useCallback(
        (schemaField, fieldId) =>
            isDetailStandardFieldVisible(schemaField, fieldId, isFieldVisible),
        [isFieldVisible],
    );

    const filterExtraFields = useCallback(
        (fields, options = {}) =>
            filterDetailExtraFieldsByDynamism(fields, {
                ...dynamismContext,
                // Stale context still has loading:true from the live pass — force ready path.
                ...(hasEffectiveShadowCrud ? { loading: false } : {}),
                ...options,
            }),
        [dynamismContext, hasEffectiveShadowCrud],
    );

    return {
        getFieldProps,
        isFieldVisible,
        isFieldReadOnly,
        isStandardFieldVisible,
        filterExtraFields,
        hasShadowCrud: hasEffectiveShadowCrud,
        isShadowCrudReady: hasEffectiveShadowCrud,
        data: dynamismContext.data,
        configMap: dynamismContext.configMap,
        mappedSchema: dynamismContext.mappedSchema,
        dynamicHiddenMap: dynamismContext.dynamicHiddenMap,
    };
};
